import type { CardId, Suit } from './cards';
import type { RulesConfig, TrumpMode } from './rules';

/** Seat index in anticlockwise play order. Seat 0 is team A, 1 is team B, alternating. */
export type Seat = number;
export type Team = 0 | 1;

export const teamOf = (seat: Seat): Team => (seat % 2) as Team;
export const nextSeat = (seat: Seat, players: number): Seat => (seat + 1) % players;

export interface Play {
  seat: Seat;
  cardId: CardId;
  /** Position within the trick, 0 = lead. */
  order: number;
}

/**
 * NONE: Cut mode before anyone cuts. HIDDEN: Band reserve face-down. REVEALED: Band reserve exposed.
 * SET: Cut established. OPEN: Random open trump shown before the deal.
 */
export type TrumpStatus = 'NONE' | 'HIDDEN' | 'REVEALED' | 'SET' | 'OPEN';

export interface TrumpState {
  mode: TrumpMode;
  status: TrumpStatus;
  suit: Suit | null;
  reserverSeat: Seat | null;
  /** The card that established or revealed trump. Never the hidden reserve while HIDDEN. */
  cardId: CardId | null;
  /** First (trick, order) from which trump-suit cards are eligible — evaluated, never stored on plays. */
  activatedAt: { trick: number; order: number } | null;
  /** BAND_B: seat that requested the reveal and must play a trump if able. */
  requestedBy: Seat | null;
}

export interface TrickRecord {
  trickIndex: number;
  plays: Play[];
  winner: Seat;
  tens: number;
}

export interface HandResult {
  handNumber: number;
  handId: string;
  dealer: Seat;
  nextDealer: Seat;
  winnerTeam: Team | null;
  draw: boolean;
  tens: [number, number];
  tricks: [number, number];
  /** All tens captured (Mendikot / Coat; Double-Coat on two-pack tables). */
  coat: boolean;
  /** All tricks captured. */
  whitewash: boolean;
  points: number;
  trumpSuit: Suit | null;
}

export type HandPhase = 'RESERVE' | 'PLAY' | 'DONE';

export interface HandState {
  handNumber: number;
  handId: string;
  dealer: Seat;
  phase: HandPhase;
  /** Playable cards per seat; the Band reserve is held separately. */
  hands: CardId[][];
  /** Cards still to be dealt (only non-empty during the Band reserve step). */
  undealt: CardId[];
  /** Hidden Band reserve, owned by trump.reserverSeat. */
  reserved: CardId | null;
  trump: TrumpState;
  leader: Seat;
  active: Seat;
  trickIndex: number;
  trick: Play[];
  captured: [CardId[], CardId[]];
  tricksWon: [number, number];
  tricks: TrickRecord[];
}

export type MatchPhase = 'IN_HAND' | 'HAND_OVER' | 'MATCH_OVER';

export interface MatchState {
  rules: RulesConfig;
  matchId: string;
  phase: MatchPhase;
  /** Dealer of the current hand (or of the next hand once HAND_OVER). */
  dealer: Seat;
  score: [number, number];
  handsPlayed: number;
  hand: HandState;
  results: HandResult[];
  winner: Team | null;
}

export type Action =
  | { type: 'RESERVE_TRUMP'; cardId: CardId }
  | { type: 'PLAY_CARD'; cardId: CardId }
  | { type: 'REQUEST_REVEAL' }
  | { type: 'NEXT_HAND' };

export type RejectCode =
  | 'WRONG_PHASE'
  | 'NOT_YOUR_TURN'
  | 'NOT_IN_HAND'
  | 'CARD_RESERVED'
  | 'MUST_FOLLOW_SUIT'
  | 'MUST_PLAY_TRUMP'
  | 'NOT_RESERVER'
  | 'REVEAL_NOT_ALLOWED'
  | 'MATCH_OVER'
  | 'ILLEGAL_ACTION';

export type RevealTrigger = 'VOID' | 'FORCED' | 'REQUEST';

export type GameEvent =
  | {
      t: 'HAND_STARTED';
      handNumber: number;
      handId: string;
      dealer: Seat;
      trumpMode: TrumpMode;
      openTrumpSuit: Suit | null;
      handSize: number;
    }
  | { t: 'CARDS_DEALT'; packet: number; size: number; counts: number[] }
  | { t: 'HAND_RECEIVED'; packet: number; cards: CardId[] }
  | { t: 'RESERVE_REQUIRED'; seat: Seat }
  | { t: 'TRUMP_RESERVED'; seat: Seat }
  | { t: 'RESERVED_CARD'; cardId: CardId }
  | { t: 'TURN_STARTED'; seat: Seat; trickIndex: number }
  | { t: 'CARD_PLAYED'; seat: Seat; cardId: CardId; trickIndex: number; order: number }
  | { t: 'TRUMP_SET'; seat: Seat; suit: Suit; cardId: CardId }
  | {
      t: 'TRUMP_REVEALED';
      reserverSeat: Seat;
      bySeat: Seat;
      suit: Suit;
      cardId: CardId;
      trigger: RevealTrigger;
    }
  | {
      t: 'TRICK_WON';
      trickIndex: number;
      winner: Seat;
      team: Team;
      cards: CardId[];
      tens: number;
    }
  | { t: 'HAND_SCORED'; result: HandResult; score: [number, number] }
  | { t: 'MATCH_COMPLETE'; winner: Team; score: [number, number] };

/** An event with its audience: everyone, or one seat's private channel. */
export interface TaggedEvent {
  to: 'public' | Seat;
  e: GameEvent;
}

export type ApplyResult =
  { ok: true; state: MatchState; events: TaggedEvent[] } | { ok: false; code: RejectCode };
